import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Save, Users, AlertTriangle, Lock } from "lucide-react";
import { api, errMsg, fmt } from "@/lib/api";
import { Stepper, QuickPick } from "@/components/fly/Stepper";
import { PenaltyRow } from "@/components/fly/PenaltyRow";
import { CategoryChangeDialog } from "@/components/fly/CategoryChangeDialog";

export default function ScoreCard() {
  const LAST_KEY = "flycup_judge_last_cat";
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [vals, setVals] = useState([]);
  const [pen, setPen] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [rk, setRk] = useState(0);
  const [changeFrom, setChangeFrom] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/judge/performances/${id}`).then((r) => {
      const d = r.data;
      setData(d);
      setVals(d.score ? d.score.sections.map((s) => s.values) : d.card.sections.map((s) => Array(s.criteria.length).fill(null)));
      setShowMissing(false);
      setPen(Object.fromEntries(d.card.penalties.map((p) => [p.key, d.score?.penalties?.[p.key] || 0])));
      setNote(d.score?.note || "");
      const p = d.performance;
      const canScore = !d.score && !d.stage_lock && p.present;
      const last = JSON.parse(localStorage.getItem(LAST_KEY) || "null");
      const from = last ? ((last.c !== p.category || last.a !== p.age_group) ? { from_category: last.c, from_age_group: last.a } : null) : p.category_change;
      setChangeFrom(canScore ? from || null : null);
      if (!d.stage_lock) localStorage.setItem(LAST_KEY, JSON.stringify({ c: p.category, a: p.age_group }));
      window.scrollTo(0, 0);
    });
  }, [id, rk]);

  useEffect(() => {
    let key = null;
    let busy = false;
    let lock = null;
    let scored = false;
    const t = setInterval(() => {
      if (busy) return;
      busy = true;
      api.get("/judge/stage").then(async (s) => {
        const c = s.data.current;
        const k = `${s.data.on_break}|${c?.id}`;
        if (k !== key) {
          const r = await api.get(`/judge/performances/${id}`);
          setData((d) => (d && d.performance.id === id ? { ...d, stage_lock: r.data.stage_lock } : d));
          key = k;
          lock = r.data.stage_lock;
          scored = !!r.data.score;
        }
        const free = !dirtyRef.current || scored || !!lock;
        if (free && !s.data.on_break && s.data.assigned && c && c.id !== id && c.present && !c.scored) nav(`/sedzia/ocena/${c.id}`, { replace: true });
      }).catch(() => {}).finally(() => { busy = false; });
    }, 1000);
    return () => clearInterval(t);
  }, [id, nav]);

  const totals = useMemo(() => cardTotals(data?.card, vals, pen), [data, vals, pen]);
  const dirtyRef = useRef(false);
  dirtyRef.current = !data?.score && (vals.flat().some((x) => x != null) || Object.values(pen).some(Boolean) || !!note);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  const perf = data.performance;
  const card = data.card;
  const locked = !perf.present || !!data.score || !data.assigned || !!data.stage_lock;

  const save = async () => {
    const missing = vals.flat().filter((x) => x == null).length;
    if (missing) {
      setShowMissing(true);
      document.querySelector("[data-missing='true']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error(`Uzupełnij wszystkie kryteria (brakuje: ${missing})`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/judge/scores/${id}`, { values: vals, penalties: pen, note });
      toast.success(`Zapisano ocenę: ${fmt(totals.total)} pkt`);
      nav("/sedzia");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const setAt = (si) => (i) => (v) => setVals((all) => all.map((arr, k) => (k === si ? arr.map((x, j) => (j === i ? v : x)) : arr)));

  return (
    <div className="pb-32">
      <ScoreHeader perf={perf} totals={totals} data={data} nav={nav} scored={!!data.score} />
      <CategoryChangeDialog perf={{ ...perf, category_change: changeFrom }} open={!!changeFrom} onClose={() => setChangeFrom(null)} />
      {data.score && (
        <div data-testid="score-done-banner" className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-xl border border-emerald-500/50 bg-emerald-500/10 p-4 text-emerald-200 text-sm flex items-center gap-3">
            <Lock className="w-5 h-5 shrink-0" />
            Ocena została zapisana i jest zablokowana. Poprawki może wprowadzić tylko organizator.
          </div>
        </div>
      )}
      {!data.score && data.assigned && data.stage_lock && (
        <div data-testid="score-stage-lock-banner" className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-xl border border-amber-500/50 bg-amber-500/15 p-4 text-amber-200 flex items-center gap-3">
            <Lock className="w-6 h-6 shrink-0" /><span className="font-display text-xl uppercase font-bold">{data.stage_lock}</span>
          </div>
        </div>
      )}
      {!data.score && !data.assigned && (
        <div data-testid="score-unassigned-banner" className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 text-amber-200 text-sm flex items-center gap-3">
            <Lock className="w-5 h-5 shrink-0" />Nie jesteś przypisany do oceniania w tym dniu.
          </div>
        </div>
      )}
      {!data.score && data.assigned && !perf.present && (
        <div data-testid="score-locked-banner" className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-xl border border-red-500/50 bg-red-500/10 p-4 text-red-200 text-sm flex flex-col sm:flex-row sm:items-center gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            Ocenianie zablokowane – zawodnik nie został zarejestrowany na miejscu. Poproś rejestrację o potwierdzenie obecności.
            <button data-testid="score-recheck-btn" onClick={() => setRk((x) => x + 1)} className="ml-auto shrink-0 h-10 px-4 rounded-lg border border-red-400/60 hover:bg-red-500/20 font-semibold">Sprawdź ponownie</button>
          </div>
        </div>
      )}
      <main inert={locked} className={`max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-2 gap-6 ${locked ? "opacity-60 pointer-events-none select-none" : ""}`}>
        <p data-testid="score-card-name" className="lg:col-span-2 font-mono text-xs uppercase tracking-[0.2em] text-blue-400">Karta punktacji: {card.name}</p>
        {card.sections.map((s, si) => (
          <CriteriaBlock key={si} title={s.name} max={s.criteria.length * 10} names={s.criteria} values={vals[si] || []} onSet={setAt(si)} prefix={`sec${si}`} sum={totals.secs[si] || 0} required showMissing={showMissing} />
        ))}
        {card.penalties.length > 0 && <section className="fly-card p-5 lg:col-span-2">
          <BlockHead title="Odjęcia punktowe" value={totals.p ? `−${fmt(totals.p)}` : "0"} red />
          <div className="grid md:grid-cols-2 gap-x-8">
            {card.penalties.map((d) => (
              <PenaltyRow key={d.key} def={d} value={pen[d.key] || 0} onChange={(v) => setPen((p) => ({ ...p, [d.key]: v }))} />
            ))}
          </div>
        </section>}
        <section className="fly-card p-5 lg:col-span-2" data-testid="score-summary">
          <BlockHead title="Podsumowanie oceny" value={`${fmt(totals.total)} pkt`} />
          <table className="w-full text-sm">
            <tbody>
              {card.sections.map((s, si) => <SumRow key={si} label={s.name} value={fmt(totals.secs[si] || 0)} max={s.criteria.length * 10} testid={`summary-sec-${si}`} />)}
              <SumRow label="Odjęcia punktowe" value={totals.p ? `−${fmt(totals.p)}` : "0"} red testid="summary-penalties" />
              <tr className="border-t border-blue-500/30">
                <td className="py-3 font-bold">Wynik końcowy</td>
                <td data-testid="summary-total" className="py-3 text-right font-display text-3xl font-black">{fmt(totals.total)}</td>
              </tr>
            </tbody>
          </table>
          <label className="block mt-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">Komentarz sędziego do występu</span>
            <textarea data-testid="score-note-input" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)}
              placeholder="Uwagi do występu, mocne strony, co poprawić… (opcjonalnie)"
              className="mt-2 w-full min-h-[110px] rounded-xl bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none p-3 text-sm" />
          </label>
        </section>
      </main>
      <footer className="fixed bottom-0 inset-x-0 z-30 backdrop-blur-xl bg-[#0B132B]/90 border-t border-blue-500/20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
          <div className="flex-1 hidden sm:block" />
          <button data-testid="score-save-btn" disabled={saving || locked} onClick={() => save()}
            className="flex-1 sm:flex-none justify-center h-14 px-6 sm:px-10 rounded-xl bg-blue-600 hover:bg-blue-500 font-display text-lg font-bold uppercase tracking-wide shadow-lg shadow-blue-600/30 flex items-center gap-2 disabled:opacity-50 active:scale-95 transition-transform">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Zapisz
          </button>
        </div>
      </footer>
    </div>
  );
}

const emptyTotals = { secs: [], pts: 0, p: 0, total: 0 };

export const cardTotals = (card, vals, pen) => {
  if (!card) return emptyTotals;
  const secs = vals.map((arr) => arr.reduce((s, x) => s + (x ?? 0), 0));
  const pts = secs.reduce((a, b) => a + b, 0);
  const p = card.penalties.reduce((s, d) => s + (d.kind === "points" ? (pen[d.key] || 0) : (pen[d.key] || 0) * d.unit), 0);
  return { secs, pts, p, total: Math.max(0, pts - p) };
};

const ScoreHeader = ({ perf, totals, data, nav, scored }) => (
  <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B132B]/85 border-b border-blue-500/20">
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
      <button data-testid="score-back-btn" onClick={() => nav("/sedzia")} className="h-11 w-11 rounded-xl border border-white/10 flex items-center justify-center hover:bg-white/5"><ArrowLeft className="w-5 h-5" /></button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs font-mono text-blue-400 uppercase tracking-wider">
          <span>{perf.time || `#${perf.order}`}</span><span className="truncate">{perf.category} · {perf.age_group}</span>
          {perf.is_team && <Users className="w-3.5 h-3.5 text-cyan-400" />}
          {scored && <span className="text-emerald-400">· oceniono</span>}
        </div>
        <div data-testid="score-performer-name" className="font-display text-xl sm:text-2xl font-bold uppercase truncate">{perf.name}</div>
        <div className="text-xs text-slate-400 truncate">{perf.school}</div>
        {!perf.present && <div data-testid="score-not-registered" className="mt-1 inline-flex items-center gap-1 text-xs text-red-300"><AlertTriangle className="w-3.5 h-3.5" />Zawodnik nie został zarejestrowany na miejscu</div>}
      </div>
      <div className="hidden md:flex gap-1">
        <button data-testid="score-prev-btn" disabled={!data.prev_id} onClick={() => nav(`/sedzia/ocena/${data.prev_id}`)} className="h-11 w-11 rounded-xl border border-white/10 flex items-center justify-center disabled:opacity-30 hover:bg-white/5"><ChevronLeft className="w-5 h-5" /></button>
        <button data-testid="score-next-btn" disabled={!data.next_id} onClick={() => nav(`/sedzia/ocena/${data.next_id}`)} className="h-11 w-11 rounded-xl border border-white/10 flex items-center justify-center disabled:opacity-30 hover:bg-white/5"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="text-right pl-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Suma</div>
        <div data-testid="score-live-total" className="font-display text-3xl sm:text-4xl font-black text-white tabular-nums leading-none">{fmt(totals.total)}</div>
      </div>
    </div>
  </header>
);

const SumRow = ({ label, value, max, red, testid }) => (
  <tr className="border-b border-white/5">
    <td className="py-2 text-slate-300">{label}</td>
    <td data-testid={testid} className={`py-2 text-right font-mono ${red ? "text-red-400" : ""}`}>{value}{max ? ` / ${max}` : ""}</td>
  </tr>
);

const BlockHead = ({ title, value, max, red }) => (
  <div className="flex items-baseline justify-between mb-2">
    <h2 className="font-display text-xl font-bold uppercase tracking-wide">{title}</h2>
    <span className={`font-mono text-sm ${red ? "text-red-400" : "text-blue-300"}`}>{value}{max ? ` / ${max}` : ""}</span>
  </div>
);

export const CriteriaBlock = ({ title, max, names, values, onSet, prefix, sum, required, showMissing }) => (
  <section className="fly-card p-5" data-testid={`${prefix}-block`}>
    <BlockHead title={title} value={fmt(sum)} max={max} />
    {required && (
      <p data-testid={`${prefix}-required-info`} className={`text-xs mb-1 ${showMissing && values.some((v) => v == null) ? "text-red-400" : "text-slate-500"}`}>
        Wymagane: oceń każde kryterium ({values.filter((v) => v != null).length}/{values.length})
      </p>
    )}
    {names.map((n, i) => {
      const miss = required && showMissing && values[i] == null;
      return (
        <div key={`${i}-${n}`} data-missing={miss ? "true" : undefined} data-testid={`${prefix}-row-${i}`}
          className={`py-3 border-b border-white/5 last:border-0 ${miss ? "bg-red-500/10 -mx-3 px-3 rounded-lg ring-1 ring-red-500/60" : ""}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-slate-200 flex-1"><span className="font-mono text-blue-400 mr-2">{i + 1}.</span>{n}{miss && <span className="ml-2 text-xs text-red-400">wybierz ocenę</span>}</div>
            <Stepper value={values[i]} onChange={onSet(i)} testid={`${prefix}-${i}`} />
          </div>
          <QuickPick value={values[i]} onChange={onSet(i)} testid={`${prefix}-${i}`} />
        </div>
      );
    })}
  </section>
);
